import type { Locale } from '@/interfaces/Locale';
import type { CRMAgentDetails, LeadGenAgentDetails } from '@/interfaces/PegaAgent';
import type { RouteQueryParams } from '@/interfaces/SocialFormProps';
import { removeHREFFromAnchors } from '@/utils/common';
import { getHostAndDomain } from '@/utils/env';
import { fetchData } from '@/utils/fetchData';
import { defineStore } from 'pinia';
import type { RouteLocationNormalizedLoaded, Router } from 'vue-router';

const { pega } = window.appConfig;

const PegaApiStore = defineStore('PegaApi', {
  state: () => ({
    pegaCRMJSON: null as CRMAgentDetails | null,
    pegaLeadGenJSON: null as LeadGenAgentDetails | null,
    pegaReturnHTML: null as null | string,
    currentHostAndDomain: getHostAndDomain(),
    locale: '' as Locale,
    pageTitle: '',
  }),
  actions: {
    async setCRMAgentDetails(isPreview: boolean, routerObj: Router) {
      const response = await fetchData<CRMAgentDetails>(
        `${window.appConfig.pega.pegaCRMAgentUrl}?id=${routerObj.currentRoute.value.params.id}`,
        routerObj
      );
      if (!response) return;
      this.pegaCRMJSON = response;
      if (this.pegaCRMJSON.fileurl)
        await this.setLifeStyleArticleHTML(this.pegaCRMJSON.fileurl, isPreview);
      if (this.pegaCRMJSON.defaultlanguage)
        this.locale = this.pegaCRMJSON.defaultlanguage.includes('fr')
          ? ('fr-CA' as Locale)
          : (this.pegaCRMJSON.defaultlanguage.split('_').join('-') as Locale);
      if (this.pegaCRMJSON.contentname) {
        this.pageTitle = this.pegaCRMJSON.contentname;
      }
    },
    async setLeadGenAgentDetails(
      vanity: string,
      smpostname: string,
      route: RouteLocationNormalizedLoaded,
      routerObj: Router
    ) {
      const response = await fetchData<LeadGenAgentDetails>(
        `${window.appConfig.pega.pegaLeadGenAgentUrl}?vanityid=${vanity}&language=${route.query.lang}&smpostname=${smpostname}`,
        routerObj
      );
      if (!response) return;
      if (response.fileurl) await this.setLifeStyleArticleHTML(response.fileurl);
      if (response.language)
        this.locale =
          `${response.language.startsWith('SP') ? 'es' : response.language.split('-')[0].toLocaleLowerCase()}-${response.language.startsWith('FR') ? 'CA' : 'US'}` as Locale;
      this.pegaLeadGenJSON = response;
    },
    async setLifeStyleArticleHTML(url: string, isPreview?: boolean) {
      const getHTML = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'text/html',
        },
        mode: 'cors',
      }).then(
        (res) => res,
        () => null
      );
      if (getHTML?.ok) {
        const lifeArticleTemp = await getHTML.text();
        this.pegaReturnHTML = isPreview ? removeHREFFromAnchors(lifeArticleTemp) : lifeArticleTemp;
      }
    },
    handlePegaMashup(mashupOuterContainer: HTMLDivElement, params: RouteQueryParams) {
      const snippetDiv = document.createElement('div');
      snippetDiv.setAttribute('data-pega-gadgetname', 'PegaGadget');
      snippetDiv.setAttribute('data-pega-action', 'display');
      snippetDiv.setAttribute('data-pega-action-param-harnessname', 'SocialMediaLeadCapture');
      snippetDiv.setAttribute('data-pega-action-param-classname', 'PRI-FieldTech-SA-Work-Contact');
      snippetDiv.setAttribute('data-pega-action-param-model', 'SocialMediaLeadCaptureInfo');
      snippetDiv.setAttribute('data-pega-action-param-readonly', 'true');
      snippetDiv.setAttribute('data-pega-isdeferloaded', 'false');
      snippetDiv.setAttribute('data-pega-applicationname', 'SalesAutomation');
      snippetDiv.setAttribute('data-pega-threadname', 'SocialMediaLeadCapture');
      snippetDiv.setAttribute('data-pega-channelID', pega.channelId);
      snippetDiv.setAttribute('data-pega-resizetype', 'stretch');
      snippetDiv.setAttribute('style', 'overflow: hidden; height: 100%;');
      snippetDiv.setAttribute('data-pega-url', pega.pegaMashupUrl);
      snippetDiv.setAttribute(
        'data-pega-action-param-parameters',
        JSON.stringify({
          pzSkinName: 'CosmosSkin',
          pyMashupSkeletonName: 'pyDefaultMashupSkeleton',
          UID: params.Vanity,
          Name: params.SMPostName,
          Language: params.Lang?.toUpperCase(),
          Platform: params.SMPlatform,
          Domain: `https://${this.currentHostAndDomain}`,
        })
      );
      mashupOuterContainer.appendChild(snippetDiv);
      const mashupDocument = mashupOuterContainer.ownerDocument;
      const mashupWindow = mashupDocument.defaultView ?? window;
      const registerPegaCommunicator = () => {
        const register = mashupWindow.pega?.Mashup?.Communicator?.register;
        if (register)
          register(
            mashupWindow.pega?.Mashup?.hostActionsProcessor as Parameters<typeof register>[0]
          );
      };

      if (mashupWindow.pega?.Mashup) {
        registerPegaCommunicator();
        mashupWindow._initAllPegaObjects?.();
      } else {
        const mashupScriptElement = mashupDocument.createElement('script');
        mashupScriptElement.onload = function () {
          registerPegaCommunicator();
          mashupWindow._initAllPegaObjects?.();
        };
        mashupScriptElement.src = `${pega.pegaMashupUrl}?pyActivity=pzIncludeMashupScripts`;
        mashupDocument.head.appendChild(mashupScriptElement);
      }
    },
  },
});

export default PegaApiStore;
